import {
  getPaxSessionRelativeUrl,
  getSessionRelativeUrl,
} from "features/shared/hooks/navigation/useFlowRedirect";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import { getGeneralAnalyticsParams } from "features/shared/hooks/analytics/useGeneralAnalytics";
import usePaxSession from "features/shared/hooks/usePaxSession";
import Logging from "logging";
import { useCallback, useState } from "react";
import AnalyticsService from "services/analytics/analytics";
import DataProvider from "services/data-provider/data-provider";
import { useAppStore } from "store/store";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import { SessionIneligibilityReasonFromApi } from "models/session-ineligibility-reason";

const useSessionConsent = (): [(consentGiven: boolean) => void, boolean] => {
  const locale = useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSessions = useAppStore((state) => state.paxSessions);
  const paxSession = usePaxSession();

  const journey = useAppStore((state) => state.journey);

  const [path, title, location] = useMutatedPagePath();

  const navigate = useNavigateWithQuery();

  const updateSession = useAppStore((state) => state.updateSession);

  const setIneligibilityReason = useAppStore(
    (state) => state.setIneligibilityReason
  );

  const setSessionIsLocked = useAppStore((state) => state.setSessionIsLocked);

  const [isLoading, setLoading] = useState(false);

  const giveConsent = useCallback(
    async (consentGiven: boolean) => {
      if (isLoading) {
        return;
      }

      if (!sessionId) {
        Logging.log("consent-form", "missing", `Session id not found in url`);
        return;
      }

      setLoading(true);

      try {
        const updatedSession = await DataProvider.shared.submitConsent(
          sessionId,
          consentGiven
        );

        AnalyticsService.shared.setConsentStatus(consentGiven);

        // This means that the user has changed their mind and revoked consent
        if (!consentGiven && paxSession && paxSession?.consented) {
          AnalyticsService.shared.trackEvent(
            "consent_revoked",
            {
              ...getGeneralAnalyticsParams(
                paxSessions,
                journey,
                path,
                title,
                location,
                locale
              ),
            },
            false // isScanRelated
          );
        }

        updateSession(updatedSession);

        if (updatedSession.paxSessions.length === 1) {
          // single passenger, navigate to their checklist
          navigate(
            `${getPaxSessionRelativeUrl(
              sessionId,
              updatedSession.paxSessions[0].paxSessionId
            )}/checklist`
          );
          return;
        }

        // multi-pax - now need to select a passenger
        navigate(`${getSessionRelativeUrl(sessionId)}/select-passenger`);
        return;
      } catch (err) {
        if (
          err instanceof DataProviderGeneralException &&
          err.type !== DataProviderExceptionType.Unknown &&
          err.type !== DataProviderExceptionType.BadRequest
        ) {
          // api "general" error
          switch (err.type) {
            case DataProviderExceptionType.NotProcessable:
            case DataProviderExceptionType.NotEligible:
              // not processable or not eligible
              setIneligibilityReason(
                SessionIneligibilityReasonFromApi(err.message)
              );
              return;

            case DataProviderExceptionType.SessionIsFinalised:
            case DataProviderExceptionType.NotFound:
              // booking not found or session is finalised
              navigate("/not-found");
              return;

            case DataProviderExceptionType.SessionIsLocked:
              setSessionIsLocked(true);
              return;
          }
        }

        setLoading(false);

        Logging.log(
          "consent-form",
          sessionId,
          `Failed to submit consent: ${
            err instanceof Error ? err.message : err
          }`
        );
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, sessionId, updateSession, navigate]
  );

  return [giveConsent, isLoading];
};

export default useSessionConsent;
